import type { SchemaData } from '../schema/types'
import type { LineDiagramSpec } from './LineDiagram'
import { lineSlugGraph } from './lineSlugGraph'

/**
 * Rail diagram of a line that is one straight path (Bakerloo, Victoria,
 * Jubilee, …): a single rail from one terminus to the other. Listed from the
 * terminus further west (smaller map x), north first on a tie — the order
 * the line pages already use. Returns `null` for a branched line, a loop or
 * a line with fewer than two stations; those need a hand-written diagram.
 */
export function autoLineDiagram(schema: SchemaData, lineId: string): LineDiagramSpec | null {
  const { stationBySlug, neighbours } = lineSlugGraph(schema, lineId)
  if (stationBySlug.size < 2) return null
  const termini: string[] = []
  for (const [slug, next] of neighbours) {
    if (next.size === 0 || next.size > 2) return null
    if (next.size === 1) termini.push(slug)
  }
  if (termini.length !== 2) return null
  const position = (slug: string) => stationBySlug.get(slug)!
  termini.sort((a, b) => position(a).x - position(b).x || position(a).y - position(b).y)

  const order = [termini[0]]
  let previous: string | undefined
  let current = termini[0]
  while (current !== termini[1]) {
    const next = [...neighbours.get(current)!].find((slug) => slug !== previous)!
    previous = current
    current = next
    order.push(current)
  }
  // A path with two termini and every other station of degree 2 visits all
  // stations unless the graph also holds a separate cycle.
  if (order.length !== stationBySlug.size) return null

  return {
    rails: 1,
    steps: order.map((slug, index) => ({
      slug,
      rail: 0,
      ...(index === 0 ? { op: 'start' as const } : {}),
      ...(index === order.length - 1 ? { op: 'end' as const } : {}),
    })),
  }
}
